import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  documentId,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';
import { DEFAULT_WORKSPACE_COLORS } from '../constants/workspaces';

/**
 * Create a new workspace
 * @param {string} userId - Firebase Auth UID
 * @param {string} accountId - Account ID (optional for backward compatibility)
 * @param {object} brandData - Brand data (name, description, logoUrl, colors)
 * @returns {Promise<string>} - Workspace ID
 */
export const createWorkspace = async (userId, accountId, brandData) => {
  // Input validation
  if (!userId || typeof userId !== 'string') {
    throw new Error('Invalid userId: must be a non-empty string');
  }
  if (!brandData?.name || typeof brandData.name !== 'string') {
    throw new Error('Workspace name is required and must be a string');
  }

  try {
    const workspaceRef = doc(collection(db, 'workspaces'));
    const userWorkspaceRef = doc(db, 'users', userId, 'workspaces', workspaceRef.id);

    // Use batch for atomicity - prevents orphaned workspace if join record fails
    const batch = writeBatch(db);

    const workspaceData = {
      ownerId: userId,
      name: brandData.name,
      description: brandData.description || '',
      logoUrl: brandData.logoUrl || null,
      colors: brandData.colors || DEFAULT_WORKSPACE_COLORS,
      members: [userId],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // Add accountId if provided (account-aware workspace)
    if (accountId) {
      workspaceData.accountId = accountId;
    }

    batch.set(workspaceRef, workspaceData);

    const userWorkspaceData = {
      role: 'owner',
      joinedAt: serverTimestamp()
    };

    // Add accountId to join record if provided
    if (accountId) {
      userWorkspaceData.accountId = accountId;
    }

    batch.set(userWorkspaceRef, userWorkspaceData);

    await batch.commit();
    return workspaceRef.id;
  } catch (error) {
    console.error('Error creating workspace:', error);
    throw error;
  }
};

/**
 * Get all workspaces the user has access to
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<Array>} - Array of workspace objects with IDs
 */
export const getUserWorkspaces = async (userId) => {
  try {
    // Query the join table to get workspace IDs
    const userWorkspacesRef = collection(db, 'users', userId, 'workspaces');
    const snapshot = await getDocs(userWorkspacesRef);

    if (snapshot.empty) {
      return [];
    }

    // Collect workspace IDs and roles
    const workspaceIds = [];
    const roleMap = {};
    snapshot.docs.forEach(docSnap => {
      workspaceIds.push(docSnap.id);
      roleMap[docSnap.id] = docSnap.data().role;
    });

    // Batch fetch workspaces using 'in' operator (limit: 10 per query)
    const workspaces = [];
    const CHUNK_SIZE = 10;

    for (let i = 0; i < workspaceIds.length; i += CHUNK_SIZE) {
      const chunk = workspaceIds.slice(i, i + CHUNK_SIZE);
      const q = query(
        collection(db, 'workspaces'),
        where(documentId(), 'in', chunk)
      );
      const workspaceSnapshot = await getDocs(q);

      workspaceSnapshot.docs.forEach(docSnap => {
        workspaces.push({
          id: docSnap.id,
          ...docSnap.data(),
          userRole: roleMap[docSnap.id]
        });
      });
    }

    // Sort by createdAt descending (newest first)
    return workspaces.sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() || 0;
      const bTime = b.createdAt?.toMillis?.() || 0;
      return bTime - aTime;
    });
  } catch (error) {
    console.error('Error getting user workspaces:', error);
    return [];
  }
};

/**
 * Get single workspace by ID
 * @param {string} workspaceId - Workspace ID
 * @returns {Promise<object|null>} - Workspace object with ID or null if not found
 */
export const getWorkspace = async (workspaceId) => {
  try {
    const workspaceDoc = await getDoc(doc(db, 'workspaces', workspaceId));

    if (!workspaceDoc.exists()) {
      return null;
    }

    return { id: workspaceDoc.id, ...workspaceDoc.data() };
  } catch (error) {
    console.error('Error getting workspace:', error);
    return null;
  }
};

/**
 * Update workspace fields
 * @param {string} workspaceId - Workspace ID
 * @param {object} updates - Fields to update (name, description, logoUrl, colors)
 * @param {string} userId - User ID performing the update (for authorization)
 * @returns {Promise<void>}
 */
export const updateWorkspace = async (workspaceId, updates, userId) => {
  // Input validation
  if (!workspaceId || typeof workspaceId !== 'string') {
    throw new Error('Invalid workspaceId: must be a non-empty string');
  }
  if (!updates || typeof updates !== 'object') {
    throw new Error('Invalid updates: must be an object');
  }
  if (!userId || typeof userId !== 'string') {
    throw new Error('Invalid userId: must be a non-empty string');
  }

  try {
    // Check workspace exists
    const workspace = await getWorkspace(workspaceId);
    if (!workspace) {
      throw new Error('Workspace not found');
    }

    // Authorization: Check if user is owner or member
    if (!workspace.members?.includes(userId)) {
      throw new Error('Unauthorized: User is not a member of this workspace');
    }

    await updateDoc(doc(db, 'workspaces', workspaceId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating workspace:', error);
    throw error;
  }
};

/**
 * Delete workspace and all associated join records
 * @param {string} workspaceId - Workspace ID
 * @param {string} userId - User ID performing the delete (for authorization)
 * @returns {Promise<void>}
 * @throws {Error} - If attempting to delete user's only workspace
 */
export const deleteWorkspace = async (workspaceId, userId) => {
  // Input validation
  if (!workspaceId || typeof workspaceId !== 'string') {
    throw new Error('Invalid workspaceId: must be a non-empty string');
  }
  if (!userId || typeof userId !== 'string') {
    throw new Error('Invalid userId: must be a non-empty string');
  }

  try {
    // Get workspace to find all members
    const workspace = await getWorkspace(workspaceId);

    if (!workspace) {
      throw new Error('Workspace not found');
    }

    // Authorization: Only owner can delete workspace
    if (workspace.ownerId !== userId) {
      throw new Error('Unauthorized: Only the workspace owner can delete it');
    }

    // Check if this is the owner's only workspace
    const ownerWorkspaces = await getUserWorkspaces(userId);

    if (ownerWorkspaces.length === 1) {
      throw new Error('Cannot delete your only workspace');
    }

    // Delete join records for all members
    for (const memberId of workspace.members) {
      try {
        await deleteDoc(doc(db, 'users', memberId, 'workspaces', workspaceId));
      } catch (error) {
        console.warn(`Error deleting join record for member ${memberId}:`, error);
      }
    }

    // Delete workspace document
    await deleteDoc(doc(db, 'workspaces', workspaceId));
  } catch (error) {
    console.error('Error deleting workspace:', error);
    throw error;
  }
};

/**
 * Set user's default workspace (remembers last active workspace)
 * @param {string} userId - Firebase Auth UID
 * @param {string} workspaceId - Workspace ID to set as default
 * @returns {Promise<void>}
 */
export const setDefaultWorkspace = async (userId, workspaceId) => {
  // Input validation
  if (!userId || typeof userId !== 'string') {
    throw new Error('Invalid userId: must be a non-empty string');
  }
  if (!workspaceId || typeof workspaceId !== 'string') {
    throw new Error('Invalid workspaceId: must be a non-empty string');
  }

  try {
    // Validate workspace exists
    const workspace = await getWorkspace(workspaceId);
    if (!workspace) {
      throw new Error('Workspace not found');
    }

    // Validate user has access to workspace (check join record)
    const userWorkspaceRef = doc(db, 'users', userId, 'workspaces', workspaceId);
    const userWorkspaceSnap = await getDoc(userWorkspaceRef);

    if (!userWorkspaceSnap.exists()) {
      throw new Error('Unauthorized: User does not have access to this workspace');
    }

    await updateDoc(doc(db, 'users', userId), {
      currentWorkspaceId: workspaceId,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error setting default workspace:', error);
    throw error;
  }
};
